import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useI18n } from 'vue-i18n';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import wootConstants from 'dashboard/constants/globals';
import {
  buildAdminSteps,
  buildAgentSteps,
  buildCustomRoleSteps,
  CHECKLIST_ITEMS,
  buildCustomRoleChecklist,
} from './tourSteps';

export const TOUR_TYPES = {
  ADMIN: 'admin',
  AGENT: 'agent',
  CUSTOM_ROLE: 'custom_role',
};

export function useOnboarding() {
  const router = useRouter();
  const getters = useStoreGetters();
  const { t } = useI18n();
  const { isAdmin } = useAdmin();
  const { uiSettings, updateUISettings } = useUISettings();

  const currentUser = computed(() => getters.getCurrentUser.value);
  const customRoleId = computed(() => getters.getCurrentCustomRoleId.value);
  const userPermissions = computed(() => {
    const accounts = currentUser.value?.accounts || [];
    const account = accounts.find(
      a => a.id === getters.getCurrentAccountId?.value
    );
    return account?.permissions || [];
  });

  const tourType = computed(() => {
    if (isAdmin.value) return TOUR_TYPES.ADMIN;
    if (customRoleId.value) return TOUR_TYPES.CUSTOM_ROLE;
    return TOUR_TYPES.AGENT;
  });

  const onboardingState = computed(() => {
    return uiSettings.value?.onboarding || {};
  });

  const tourCompleted = computed(() => {
    const state = onboardingState.value;
    if (tourType.value === TOUR_TYPES.ADMIN)
      return state?.admin_completed === true;
    if (tourType.value === TOUR_TYPES.AGENT)
      return state?.agent_completed === true;
    if (tourType.value === TOUR_TYPES.CUSTOM_ROLE) {
      return (
        state?.custom_role_completed === true &&
        state?.custom_role_permissions_hash ===
          JSON.stringify(userPermissions.value?.sort())
      );
    }
    return false;
  });

  const currentStep = computed(() => {
    const state = onboardingState.value;
    if (tourType.value === TOUR_TYPES.ADMIN) return state?.admin_step || 0;
    if (tourType.value === TOUR_TYPES.AGENT) return state?.agent_step || 0;
    if (tourType.value === TOUR_TYPES.CUSTOM_ROLE)
      return state?.custom_role_step || 0;
    return 0;
  });

  const interfaceMode = computed(() => {
    return uiSettings.value?.interface_mode || 'simple';
  });

  const shouldShowTour = computed(() => {
    // Do not show if already completed
    if (tourCompleted.value) return false;
    // Do not show if user explicitly skipped and didn't restart
    if (onboardingState.value?.skipped === true) return false;
    // Show if step is 0 (first visit) or partial (revisit after reload)
    return true;
  });

  const getChecklist = computed(() => {
    const type = tourType.value;
    if (type === TOUR_TYPES.ADMIN) return CHECKLIST_ITEMS.admin;
    if (type === TOUR_TYPES.AGENT) return CHECKLIST_ITEMS.agent;
    return buildCustomRoleChecklist(userPermissions.value);
  });

  const getChecklistState = computed(() => {
    return onboardingState.value?.checklist || {};
  });

  const getCompletedCount = computed(() => {
    const checklist = getChecklistState.value;
    return Object.values(checklist).filter(Boolean).length;
  });

  const getTotalCount = computed(() => getChecklist.value.length);

  const driverInstance = ref(null);

  const persistOnboardingState = async partialState => {
    const existing = uiSettings.value?.onboarding || {};
    const merged = { ...existing, ...partialState };
    await updateUISettings({
      onboarding: merged,
    });
  };

  const markChecklistItem = async itemKey => {
    const existing = uiSettings.value?.onboarding?.checklist || {};
    await persistOnboardingState({
      checklist: { ...existing, [itemKey]: true },
    });
  };

  const buildSteps = () => {
    let steps;
    if (tourType.value === TOUR_TYPES.ADMIN) steps = buildAdminSteps(t);
    else if (tourType.value === TOUR_TYPES.AGENT) steps = buildAgentSteps(t);
    else steps = buildCustomRoleSteps(t);

    // On small screens the sidebar is off-canvas, so element-targeted steps
    // would highlight invisible elements. Show centered popovers instead.
    if (window.innerWidth < wootConstants.SMALL_SCREEN_BREAKPOINT) {
      steps = steps.map(step => ({ ...step, element: undefined }));
    }
    return steps;
  };

  const startTour = async () => {
    if (driverInstance.value) {
      driverInstance.value.destroy();
    }

    const steps = buildSteps();
    const startIndex = Math.min(
      currentStep.value || 0,
      Math.max(0, steps.length - 1)
    );
    let lastActiveIndex = startIndex;

    const tourDriver = driver({
      showProgress: true,
      allowClose: true,
      smoothScroll: true,
      overlayOpacity: 0.4,
      prevBtnText: t('ONBOARDING_TOUR.PREVIOUS'),
      nextBtnText: t('ONBOARDING_TOUR.NEXT'),
      doneBtnText: t('ONBOARDING_TOUR.DONE'),
      progressText: t('ONBOARDING_WIZARD.PROGRESS', {
        current: '{{current}}',
        total: '{{total}}',
      }),
      steps,
      onDestroyed: () => {
        driverInstance.value = null;
        const isComplete = lastActiveIndex >= steps.length - 1;
        if (isComplete) {
          const completedKey = `${tourType.value}_completed`;
          const permissionsHash =
            tourType.value === TOUR_TYPES.CUSTOM_ROLE
              ? JSON.stringify(userPermissions.value?.sort())
              : undefined;
          persistOnboardingState({
            [completedKey]: true,
            [`${tourType.value}_step`]: 999,
            ...(permissionsHash
              ? { custom_role_permissions_hash: permissionsHash }
              : {}),
          });
        } else {
          persistOnboardingState({
            [`${tourType.value}_step`]: lastActiveIndex,
            skipped: true,
          });
        }
      },
      onCloseClick: (element, step, { driver: activeDriver }) => {
        lastActiveIndex = activeDriver.getActiveIndex();
        activeDriver.destroy();
      },
      onNextClick: (element, step, { driver: activeDriver }) => {
        lastActiveIndex = activeDriver.getActiveIndex();
        persistOnboardingState({
          [`${tourType.value}_step`]: lastActiveIndex + 1,
        });
        activeDriver.moveNext();
      },
      onPrevClick: (element, step, { driver: activeDriver }) => {
        lastActiveIndex = activeDriver.getActiveIndex();
        persistOnboardingState({
          [`${tourType.value}_step`]: Math.max(0, lastActiveIndex - 1),
        });
        activeDriver.movePrevious();
      },
      onDeselected: () => {
        // noop
      },
      onHighlightStarted: (element, step, { driver: activeDriver }) => {
        const stepIndex = activeDriver.getActiveIndex();
        const CONVERSATION_LIST_STEP = 2;
        if (stepIndex === CONVERSATION_LIST_STEP) {
          const accountId = getters.getCurrentAccountId?.value;
          router.push({
            name: 'home',
            params: { accountId },
          });
        }
      },
    });

    driverInstance.value = tourDriver;
    tourDriver.drive(startIndex);
  };

  const skipTour = async () => {
    if (driverInstance.value) {
      driverInstance.value.destroy();
    }
    await persistOnboardingState({
      skipped: true,
    });
  };

  const completeTour = async () => {
    if (driverInstance.value) {
      driverInstance.value.destroy();
    }
    const completedKey = `${tourType.value}_completed`;
    const permissionsHash =
      tourType.value === TOUR_TYPES.CUSTOM_ROLE
        ? JSON.stringify(userPermissions.value?.sort())
        : undefined;

    await persistOnboardingState({
      [completedKey]: true,
      [`${tourType.value}_step`]: 999,
      ...(permissionsHash
        ? { custom_role_permissions_hash: permissionsHash }
        : {}),
    });
  };

  const restartTour = async () => {
    await persistOnboardingState({
      [`${tourType.value}_completed`]: false,
      [`${tourType.value}_step`]: 0,
      skipped: false,
      wizard_completed: false,
    });
  };

  const setInterfaceMode = async mode => {
    await updateUISettings({
      interface_mode: mode,
    });
  };

  return {
    tourType,
    shouldShowTour,
    currentStep,
    tourCompleted,
    interfaceMode,
    getChecklist,
    getChecklistState,
    getCompletedCount,
    getTotalCount,
    startTour,
    skipTour,
    completeTour,
    restartTour,
    markChecklistItem,
    setInterfaceMode,
    userPermissions,
    customRoleId,
  };
}
